/**
 * Configurable Document Bank taxonomy.
 *
 * docType is CONFIGURABLE: the default document kinds are defined here (not
 * baked into the type system) so the taxonomy can grow without a code change to
 * the domain types. The list ends with an "Other" escape hatch so a team can
 * capture a kind outside the defaults.
 *
 * status is a small, stable, closed union in {@link ./types}; its
 * presentation metadata (labels + badge tones) lives in {@link ./documentModel}.
 *
 * Pure TS — no React. Presentation layers map these to <Select> options.
 */

/** A single configurable taxonomy entry. */
export interface DocumentConfigOption {
  /** Stored value (also the option value). */
  value: string;
  /** Human-readable label. */
  label: string;
  /** Optional one-line helper shown in pickers. */
  hint?: string;
}

/** Sentinel value for the "Other" escape hatch. */
export const OTHER_DOC_TYPE = 'Other';

/**
 * Default document types. Ordered roughly design → operate → knowledge, ending
 * with the Other escape hatch.
 */
export const DOCUMENT_TYPES: DocumentConfigOption[] = [
  { value: 'Design Doc', label: 'Design Doc', hint: 'A technical design' },
  { value: 'RFC', label: 'RFC', hint: 'Request for comments / proposal' },
  { value: 'PRFAQ', label: 'PRFAQ', hint: 'Press release + FAQ' },
  { value: 'Architecture', label: 'Architecture', hint: 'System architecture' },
  { value: 'API Spec', label: 'API Spec', hint: 'Interface / contract' },
  { value: 'Migration Plan', label: 'Migration Plan', hint: 'A planned migration' },
  { value: 'Runbook', label: 'Runbook', hint: 'Operational procedure' },
  { value: 'Postmortem', label: 'Postmortem', hint: 'Incident retrospective / COE' },
  { value: 'Investigation', label: 'Investigation', hint: 'Analysis / deep dive' },
  { value: 'Requirements', label: 'Requirements', hint: 'What must be built' },
  { value: 'Decision Record', label: 'Decision Record', hint: 'A recorded decision' },
  { value: 'Knowledge', label: 'Knowledge', hint: 'Reference / how-to' },
  { value: OTHER_DOC_TYPE, label: 'Other', hint: 'Kind outside the defaults' },
];

/** Default docType for a brand-new document draft. */
export const DEFAULT_DOC_TYPE = 'Design Doc';

/** Human-readable label for a docType (falls back to the raw value). */
export function docTypeLabel(value: string): string {
  return DOCUMENT_TYPES.find((o) => o.value === value)?.label ?? value;
}
